import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const FAQ = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(item => item !== index)
        : [...prev, index]
    );
  };

  const faqData = [
    {
      question: "O que é o Plano Inteligente de Negócios (PIN)?",
      answer: "O PIN é o plano personalizado do seu negócio, construído e atualizado pela conversa com a AI4ME. Ele reúne diagnóstico, prioridades e próximos passos — tudo estruturado para você agir com clareza."
    },
    {
      question: "Como a AI4ME ajuda meu negócio no dia a dia?",
      answer: "Você conversa com a AI4ME sobre seus desafios e, a partir dessa conversa, ela estrutura seu Plano Inteligente de Negócios (PIN). Ele é atualizado conforme você evolui, sempre pela conversa — 24 horas por dia, 7 dias por semana."
    },
    {
      question: "Quem pode usar o serviço AI4ME?",
      answer: "Nosso serviço é ideal para micro e pequenos empreendedores: comércios locais, prestadores de serviços, consultores, artesãos, startups e profissionais autônomos. O PIN é construído pela conversa, sem exigir conhecimento técnico prévio."
    },
    {
      question: "Como funciona a AI4ME?",
      answer: "A AI4ME é um agente de IA conversacional especializado em negócios. Você conversa com ela, e a partir dessa conversa nasce o seu Plano Inteligente de Negócios (PIN) — com diagnóstico, prioridades e ações práticas. O plano é atualizado continuamente pela conversa."
    },
    {
      question: "Quais são os recursos principais do serviço?",
      answer: "Oferecemos: conversa inteligente para entender seu negócio, geração automática do PIN (Plano Inteligente de Negócios), acompanhamento de metas e prioridades, relatórios em PDF, suporte por texto e voz, e painel completo para monitoramento — tudo construído e evoluído pela conversa."
    },
    {
      question: "Como começar a usar o serviço?",
      answer: "Simples! Cadastre-se e inicie uma conversa com a AI4ME. Ela vai entender seu negócio e, quando fizer sentido, criar seu Plano Inteligente de Negócios (PIN). Primeiros insights em poucos minutos."
    },
    {
      question: "A AI4ME faz automação de processos?",
      answer: "Não. A AI4ME é uma consultora conversacional. Ela constrói e atualiza seu Plano Inteligente de Negócios (PIN) pela conversa, oferece orientações e análises — mas não executa tarefas automatizadas. Ela atua como sua parceira estratégica."
    },
    {
      question: "Vocês oferecem suporte técnico?",
      answer: "Sim! Oferecemos suporte completo através de múltiplos canais: portal de ajuda online, WhatsApp, e-mail e documentação técnica. A AI4ME também está sempre disponível para ajustar seu PIN pela conversa."
    },
    {
      question: "Qual é o investimento nos planos de assinatura?",
      answer: "Temos planos escaláveis desde básico até empresarial, com diferentes limites de recursos. Todos incluem acesso à AI4ME e a possibilidade de construir seu Plano Inteligente de Negócios pela conversa. Consulte nossa página de planos para encontrar a opção ideal."
    }
  ];

  // Schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <section id="faq" className="py-12 md:py-20 bg-muted/30">
      {/* Schema markup */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            Perguntas Frequentes
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
            Encontre respostas para as principais dúvidas sobre nosso serviço de IA por assinatura
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqData.map((item, index) => (
            <Card key={index} className="overflow-hidden">
              <CardContent className="p-0">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full p-4 md:p-6 text-left hover:bg-muted/50 transition-colors flex items-center justify-between group min-h-[44px]"
                  aria-expanded={openItems.includes(index)}
                >
                  <h3 className="text-base md:text-lg font-semibold text-foreground pr-4 group-hover:text-primary transition-colors">
                    {item.question}
                  </h3>
                  {openItems.includes(index) ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 group-hover:text-primary transition-colors" />
                  )}
                </button>
                
                {openItems.includes(index) && (
                  <div className="px-4 pb-4 md:px-6 md:pb-6 animate-fade-in">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FAQ;